<script setup lang="ts">
import BaseInputSelect from '../../../base/BaseInputSelect.vue'
import { defineEmits, defineProps, reactive, ref, RenderFunction, watch } from 'vue'
import { AccountDataState, KycDocumentVerificationType, SelectOption } from '../../../../typings'
import { FormContext, useForm } from 'vee-validate'
import { MinimalKycDocument } from '../../../../utilities/api'
import BaseKycDocumentFileUpload from '../../../base/BaseKycDocumentFileUpload.vue'

const props = defineProps<{
    verificationType: KycDocumentVerificationType
    store: AccountDataState
    icon: RenderFunction
    modelValue: MinimalKycDocument
    form: FormContext | null
    uploading: boolean
    identificationTypes: SelectOption[]
}>()

const uploadType = ref<'image' | 'pdf' | null>(null)

const form = useForm({
    validationSchema: {
        'newKycDocument.type': 'required',
    },
})
const emit = defineEmits(['update:modelValue', 'update:form', 'update:uploading'])
emit('update:form', form)

const uploading = ref(props.uploading)
watch(uploading, () => emit('update:uploading', uploading.value))

const newKycDocument = reactive<MinimalKycDocument>(props.modelValue)
watch(newKycDocument, () => {
    emit('update:modelValue', { ...newKycDocument })
})

function updateStorageKey(files) {
    if (files.length === 0) {
        newKycDocument.storage_key = null
        uploadType.value = null
    } else {
        newKycDocument.storage_key = files[0]?.key
        uploadType.value = 'image'
    }
}

function updateStorageKeyBack(files) {
    if (files.length === 0) {
        newKycDocument.storage_key_back = null
        uploadType.value = null
    } else {
        newKycDocument.storage_key_back = files[0]?.key
        uploadType.value = 'image'
    }
}

function updateStorageKeyPdf(files) {
    if (files.length === 0) {
        newKycDocument.storage_key_pdf = null
        uploadType.value = null
    } else {
        newKycDocument.storage_key_pdf = files[0]?.key
        uploadType.value = 'pdf'
    }
}
</script>

<template>
    <div>
        <BaseInputSelect
            v-model="newKycDocument.type"
            :disabled="identificationTypes.length === 1"
            name="newKycDocument.type"
            rules="required"
            :label="$t('verification_kyc_document.identification_type')"
            :icon="icon"
            :options="identificationTypes">
        </BaseInputSelect>
        <template v-if="props.verificationType === KycDocumentVerificationType.KycId">
            <h4
                v-if="uploadType === 'pdf' || uploadType === null"
                class="mb-2 mt-6 text-base font-medium leading-6 text-gray-900">
                {{ $t('verification.pdf_both_sides') }}
            </h4>
            <BaseKycDocumentFileUpload
                v-if="uploadType === 'pdf' || uploadType === null"
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document_pdf"
                class="my-2"
                :required="false"
                :required2="false"
                :camera="false"
                :accepted-file-types="['application/pdf']"
                max-file-size="5MB"
                @update:model-value="updateStorageKeyPdf" />
            <div v-if="uploadType === null" class="separator my-16 text-sm text-gray-400">
                {{ $t('camera.or') }}
            </div>
            <h4
                v-if="uploadType === 'image' || uploadType === null"
                class="mb-2 mt-10 text-base font-medium leading-6 text-gray-900">
                {{ $t('id_front') }}
            </h4>
            <BaseKycDocumentFileUpload
                v-if="uploadType === 'image' || uploadType === null"
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document"
                class="my-2"
                :camera="true"
                :required="false"
                :required2="false"
                max-file-size="5MB"
                @update:model-value="updateStorageKey" />

            <h4
                v-if="uploadType === 'image' || uploadType === null"
                class="mb-2 mt-6 text-base font-medium leading-6 text-gray-900">
                {{ $t('id_back') }}
            </h4>
            <BaseKycDocumentFileUpload
                v-if="uploadType === 'image' || uploadType === null"
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document_back"
                class="my-2"
                :camera="true"
                :required="false"
                :required2="false"
                max-file-size="5MB"
                @update:model-value="updateStorageKeyBack" />
        </template>
        <template v-else>
            <BaseKycDocumentFileUpload
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document"
                class="my-2"
                :camera="true"
                :accepted-file-types="['image/jpg', 'image/jpeg', 'image/png', 'application/pdf']"
                max-file-size="5MB"
                @update:model-value="updateStorageKey" />
        </template>
    </div>
</template>

<style scoped>
.separator {
    display: flex;
    align-items: center;
    text-align: center;
    margin-bottom: 0.6em;
}

.separator::before,
.separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #d1d5db;
}

.separator:not(:empty)::before {
    margin-right: 0.25em;
}

.separator:not(:empty)::after {
    margin-left: 0.25em;
}
</style>
